import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../theme/ThemeContext';

export default function PercentStackedBarchart({ data, points, ids, sortMethod = "abundance", onSortMethodChange }) {

    const containerRef = useRef();
    const svgRef = useRef();
    const { theme } = useTheme();
    // Add state to track the selected taxon for sorting
    const [selectedTaxon, setSelectedTaxon] = useState(null);

    // Add state to track the actual sort method being used
    const [effectiveSortMethod, setEffectiveSortMethod] = useState(sortMethod);

    const userInitiatedChangeRef = useRef(false);
    const previousSortMethodRef = useRef(sortMethod);

    // Reset selected taxon when sort method changes externally
    useEffect(() => {
        // Always update when sort method changes from the parent component
        if (sortMethod !== previousSortMethodRef.current) {
            // If switching away from abundance, or if not a user-initiated change
            if (sortMethod !== 'abundance') {
                setSelectedTaxon(null);
            }

            // Always update effective sort method on external changes
            setEffectiveSortMethod(sortMethod);

            // Reset the user change flag if this was an external change
            if (!userInitiatedChangeRef.current) {
                userInitiatedChangeRef.current = false;
            } else {
                // If this was a response to our own change, also reset
                userInitiatedChangeRef.current = false;
            }
        }

        // Update the previous sort method reference
        previousSortMethodRef.current = sortMethod;
    }, [sortMethod]);

    // Notify parent of changes
    useEffect(() => {
        // Only notify parent if this was a user-initiated change
        if (userInitiatedChangeRef.current && onSortMethodChange && effectiveSortMethod !== sortMethod) {
            onSortMethodChange(effectiveSortMethod);
        }
    }, [effectiveSortMethod, sortMethod, onSortMethodChange]);

    const handleTaxonSelection = (taxon) => {
        // If "Other" is clicked, don't sort by it
        if (taxon === "Other") return;

        // Mark this as a user-initiated change
        userInitiatedChangeRef.current = true;

        // Set sort method to abundance
        setEffectiveSortMethod("abundance");

        // Update selected taxon
        setSelectedTaxon(taxon);
    };

    const drawChart = () => {
        if (!data || data.length === 0 || !containerRef.current) return;

        // Clear previous content
        d3.select(svgRef.current).selectAll('*').remove();

        // Get container dimensions
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = Math.max(500, containerWidth * 0.6);

        const margin = {
            top: 40,
            // right: 120, // Extra space for legend
            right: 140, // Extra space for legend
            bottom: 100, // Space for x-axis labels
            left: 60
        };

        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', containerHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Process data for stacked chart
        // First, get all unique taxa across all samples
        const allTaxa = new Set();
        data.forEach(sample => {
            sample.taxonomic_data
                .filter(t => t.relative_abundance > 0.005) // Filter small values (<0.5%)
                .forEach(t => allTaxa.add(t.taxon.name));
        });

        // Sort taxa by average abundance across samples (descending)
        const sortedTaxa = Array.from(allTaxa).sort((a, b) => {
            const avgA = data.reduce((sum, sample) => {
                const filteredTaxa = sample.taxonomic_data.filter(t => t.relative_abundance > 0.005);
                const totalAbundance = filteredTaxa.reduce((total, t) => total + t.relative_abundance, 0);
                const taxonA = filteredTaxa.find(t => t.taxon.name === a);
                return sum + (taxonA ? taxonA.relative_abundance / totalAbundance : 0);
            }, 0) / data.length;

            const avgB = data.reduce((sum, sample) => {
                const filteredTaxa = sample.taxonomic_data.filter(t => t.relative_abundance > 0.005);
                const totalAbundance = filteredTaxa.reduce((total, t) => total + t.relative_abundance, 0);
                const taxonB = filteredTaxa.find(t => t.taxon.name === b);
                return sum + (taxonB ? taxonB.relative_abundance / totalAbundance : 0);
            }, 0) / data.length;

            return avgB - avgA;
        });

        // Take top 10 taxa plus "Other"
        const topTaxa = sortedTaxa.slice(0, 10);

        // Create stack data
        const stackData = data.map((sample, i) => {
            // Use display_name instead of sample_id for labels
            const result = {
                sample: sample.display_name || sample.sample_id || `Sample ${i + 1}`,
                sampleId: sample.sample_id // Keep the actual ID for potential use
            };

            let otherSum = 0;

            sample.taxonomic_data.forEach(t => {
                if (topTaxa.includes(t.taxon.name)) {
                    result[t.taxon.name] = t.relative_abundance;
                } else {
                    otherSum += t.relative_abundance;
                }
            });

            result["Other"] = otherSum;
            return result;
        });

        // Create stack generator
        const stackGenerator = d3.stack()
            .keys([...topTaxa, "Other"])
            .order(d3.stackOrderNone)
            .offset(d3.stackOffsetExpand); // This creates percent stacking (0-1)

        const stackedData = stackGenerator(stackData);

        // Sort the data based on the current sorting method and selected taxon
        if (effectiveSortMethod === "abundance") {
            const sortByTaxon = selectedTaxon || topTaxa[0]; // Use selected taxon or default to most abundant
            stackData.sort((a, b) => (b[sortByTaxon] || 0) - (a[sortByTaxon] || 0));
        }
        else if (effectiveSortMethod === "x" && points && ids) {
            // Sort by X coordinates in UMAP
            stackData.sort((a, b) => {
                const indexA = ids.indexOf(a.sampleId);
                const indexB = ids.indexOf(b.sampleId);

                if (indexA !== -1 && indexB !== -1) {
                    return points[indexA][0] - points[indexB][0];
                }

                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return 0;
            });
        }
        else if (effectiveSortMethod === "y" && points && ids) {
            // Sort by Y coordinates in UMAP
            stackData.sort((a, b) => {
                const indexA = ids.indexOf(a.sampleId);
                const indexB = ids.indexOf(b.sampleId);

                if (indexA !== -1 && indexB !== -1) {
                    return points[indexA][1] - points[indexB][1];
                }

                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return 0;
            });
        }

        const x = d3.scaleBand()
            .domain(stackData.map(d => d.sample))
            .range([0, width])
            .padding(stackData.length > 50 ? 0 : 0.2);

        // Y scale (percent)
        const y = d3.scaleLinear()
            .domain([0, 1])
            .range([height, 0]);

        // Color scale
        const color = d3.scaleOrdinal()
            .domain([...topTaxa, "Other"])
            // .range(["#688ccd", "#63b750", "#d24ca1", "#b2ae44", "#8d66cb",
            //     "#de923a", "#4fb79b", "#d14f42", "#5b7d39", "#be6487", "#ad7344"]);
            .range(["#688ccd", "#63b750", "#d24ca1", "#b2ae44", "#8d66cb",
                "#de923a", "#4fb79b", "#d14f42", "#5b7d39", "#be6487", "#808080"]);

        // Draw axes
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .selectAll('text')
            .attr('transform', 'rotate(-45)')
            .style('text-anchor', 'end')
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .style('fill', theme.colors.text)
            .style('visibility', function (d, i) {
                // If more than 50 samples, only show every nth label
                // if (stackData.length > 50) {
                if (stackData.length > 80) {
                    // Calculate how many labels to skip based on total count
                    const skipFactor = Math.ceil(stackData.length / 25);
                    return i % skipFactor === 0 ? 'visible' : 'hidden';
                }
                return 'visible';
            })
            .text(function (d) {
                // If more than 50 samples, truncate long sample names
                if (stackData.length > 50 && d.length > 10) {
                    return d.substring(0, 10) + '...';
                }
                return d;
            });

        svg.append('g')
            .call(d3.axisLeft(y).tickFormat(d => `${d * 100}%`))
            .selectAll('text')
            .style('fill', theme.colors.text);

        // Y-axis label
        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - margin.left)
            .attr('x', 0 - (height / 2))
            .attr('dy', '1em')
            .style('text-anchor', 'middle')
            .text('Relative Abundance (%)')
            .style('fill', theme.colors.text);
        // X-axis label
          svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - (width / 2))
            .attr('x', 0 - margin.bottom)
            .attr('dy', '1em')
            .style('text-anchor', 'middle')
            .text('Relative Abundance (%)')
            .style('fill', theme.colors.text);

        // Draw stacked bars
        svg.append('g')
            .selectAll('g')
            .data(stackedData)
            .enter().append('g')
            .attr('fill', d => color(d.key))
            .selectAll('rect')
            .data(d => d)
            .enter().append('rect')
            .attr('x', d => x(d.data.sample))
            .attr('y', d => y(d[1]))
            .attr('height', d => y(d[0]) - y(d[1]))
            .attr('width', x.bandwidth())
            .on('mouseover', function (event, d) {
                const taxon = d3.select(this.parentNode).datum().key;
                const percent = ((d[1] - d[0]) * 100).toFixed(1);
                const sampleName = d.data.sample;

                // Highlight bar segment
                d3.select(this)
                    .style('opacity', 0.8)
                    .style('stroke', theme.colors.text)
                    .style('stroke-width', 2);

                // Get container and rect positions for better positioning
                const containerRect = containerRef.current.getBoundingClientRect();
                const barRect = this.getBoundingClientRect();

                // Add tooltip with proper positioning
                tooltip
                    .style('opacity', 1)
                    .html(`<strong>${taxon}</strong><br>${percent}%<br>${sampleName}`);

                // Calculate tooltip position relative to container
                const tooltipNode = tooltip.node();
                const tooltipRect = tooltipNode.getBoundingClientRect();

                let tooltipX = (barRect.left - containerRect.left) + barRect.width + 10;
                let tooltipY = (barRect.top - containerRect.top) - (tooltipRect.height / 2) + (barRect.height / 2);

                // Keep tooltip within container bounds
                const maxLeft = containerRect.width - tooltipRect.width;
                const maxTop = containerRect.height - tooltipRect.height;

                tooltipX = Math.min(tooltipX, maxLeft - 5);
                tooltipY = Math.min(Math.max(tooltipY, 5), maxTop - 5);

                tooltip
                    .style('left', `${tooltipX}px`)
                    .style('top', `${tooltipY}px`);
            })
            .on('mouseout', function () {
                // Remove highlight
                d3.select(this)
                    .style('opacity', 1)
                    .style('stroke', 'none');

                // Hide tooltip
                tooltip.style('opacity', 0);
            });

        // Add legend with interactive elements
        const legend = svg.append('g')
            .attr('font-family', 'sans-serif')
            .attr('font-size', 10)
            .attr('text-anchor', 'start')
            .selectAll('g')
            // .data([...topTaxa, "Other"])
            .data(["Other", ...topTaxa.reverse()])
            .enter().append('g')
            .attr('transform', (d, i) => `translate(${width + 10},${i * 20})`)
            .style('cursor', 'pointer')
            .on('click', function (event, d) {
                handleTaxonSelection(d);
            });

        // Add rectangles to legend
        legend.append('rect')
            .attr('x', 0)
            .attr('width', 19)
            .attr('height', 19)
            .attr('fill', color)
            .style('stroke', d => (d === selectedTaxon) ? theme.colors.text : 'none')
            .style('stroke-width', d => (d === selectedTaxon) ? 2 : 0);

        // Add text to legend
        legend.append('text')
            .attr('x', 24)
            .attr('y', 9.5)
            .attr('dy', '0.32em')
            .text(d => d)
            .style('fill', theme.colors.text)
            .style('font-weight', d => (d === selectedTaxon) ? 'bold' : 'normal');

        // Add tooltip
        const tooltip = d3.select(containerRef.current)
            .append('div')
            .style('opacity', 0)
            .attr('class', 'tooltip')
            .style('position', 'absolute')
            .style('background-color', theme.colors.surface)
            .style('color', theme.colors.text)
            .style('padding', '8px')
            .style('border-radius', '4px')
            .style('pointer-events', 'none')
            .style('font-size', '12px')
            .style('z-index', 1000)
            .style('box-shadow', '0 2px 5px rgba(0,0,0,0.2)')
            .style('white-space', 'nowrap');
    };

    useEffect(() => {
        drawChart();
        const handleResize = () => drawChart();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [data, theme, sortMethod, selectedTaxon, effectiveSortMethod, points]);

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            <svg ref={svgRef} />
        </div>
    );
}
